// Whiteboard engine — the ink. A pen stroke is drawn as a FILLED OUTLINE
// whose width follows the pressure at every point (a real stylus reports it;
// a mouse or finger gets a pressure worked out from its speed when the
// stroke is captured), with round caps, round turns and a gentle taper —
// so handwriting and sketches read like ink, not like a fixed-width line.
// Path "ops" are the one drawing language of the engine:
//   ['M', x, y] ['L', x, y] ['Q', cx, cy, x, y] ['C', c1x, c1y, c2x, c2y, x, y] ['Z']
// SVG, Canvas and PDF each turn them into their own calls (draw.js).

import { dist, lerp, simplify } from './geometry.js';

const TAU = Math.PI * 2;

function arc(out, cx, cy, r, from, to, steps) {
  for (let i = 1; i < steps; i++) {
    const a = from + ((to - from) * i) / steps;
    out.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r]);
  }
}
const angleOf = (x, y) => Math.atan2(y, x);
// The signed sweep from a to b going the short way (−π..π).
function sweep(a, b) {
  let d = b - a;
  while (d > Math.PI) d -= TAU;
  while (d < -Math.PI) d += TAU;
  return d;
}

// points: [[x, y, pressure?], …]; size = the nominal width.
// opts.pen: the pressure is real (no taper is faked); opts.thinning: how much
// the pressure moves the width (0 = none).
export function strokeOutline(points, size, opts = {}) {
  const thinning = opts.thinning ?? 0.55;
  const streamline = opts.streamline ?? 0.35;
  const half = Math.max(0.25, size / 2);
  if (!points || !points.length) return [];

  // 1) low-pass the path (steadies a shaky hand) and drop crowded points
  const pts = [];
  let prev = null;
  for (const p of points) {
    const pr = p[2] == null ? 0.5 : p[2];
    if (!prev) { prev = [p[0], p[1], pr]; pts.push(prev); continue; }
    const t = 1 - streamline;
    const cur = [lerp(prev[0], p[0], t), lerp(prev[1], p[1], t), pr];
    if (dist(cur[0], cur[1], prev[0], prev[1]) < Math.max(0.3, half * 0.15)) { prev[2] = Math.max(prev[2], pr); continue; }
    pts.push(cur); prev = cur;
  }
  const last = points[points.length - 1];
  if (pts.length > 1 && dist(last[0], last[1], pts[pts.length - 1][0], pts[pts.length - 1][1]) > 0.5) {
    pts.push([last[0], last[1], last[2] == null ? 0.5 : last[2]]);
  }

  // total length → a dot, or the taper length
  let total = 0;
  for (let i = 1; i < pts.length; i++) total += dist(pts[i][0], pts[i][1], pts[i - 1][0], pts[i - 1][1]);
  const scaleOf = (p) => 1 - thinning + thinning * 2 * p;
  if (pts.length < 2 || total < half * 0.6) {
    const r = half * scaleOf(Math.max(...pts.map((p) => p[2])));
    const c = pts[0]; const out = [];
    arc(out, c[0], c[1], r, 0, TAU, 20);
    out.unshift([c[0] + r, c[1]]);
    return out;
  }

  // 2) radius at every point (pressure, smoothed; a gentle taper unless a pen said otherwise)
  const taperLen = Math.min(half * 6, total * 0.3);
  let run = 0;
  const radii = pts.map((p, i) => {
    if (i) run += dist(p[0], p[1], pts[i - 1][0], pts[i - 1][1]);
    let r = half * scaleOf(p[2]);
    if (!opts.pen && taperLen > 0) {
      const ease = (t) => 0.45 + 0.55 * Math.sin((Math.min(1, t) * Math.PI) / 2);
      r *= Math.min(ease(run / taperLen), ease((total - run) / taperLen));
    }
    return r;
  });
  for (let pass = 0; pass < 2; pass++) {
    for (let i = 1; i < radii.length - 1; i++) radii[i] = (radii[i - 1] + radii[i] * 2 + radii[i + 1]) / 4;
  }

  // 3) the two sides, with a round turn wherever the stroke doubles back
  const left = []; const right = [];
  const n = pts.length;
  const dirAt = (i) => {
    const a = pts[Math.max(0, i - 1)]; const b = pts[Math.min(n - 1, i + 1)];
    const dx = b[0] - a[0]; const dy = b[1] - a[1]; const l = Math.hypot(dx, dy) || 1;
    return [dx / l, dy / l];
  };
  for (let i = 0; i < n; i++) {
    const [x, y] = pts[i]; const r = radii[i];
    if (i > 0 && i < n - 1) {
      const a = pts[i - 1]; const b = pts[i + 1];
      let ix = x - a[0]; let iy = y - a[1]; const il = Math.hypot(ix, iy) || 1; ix /= il; iy /= il;
      let ox = b[0] - x; let oy = b[1] - y; const ol = Math.hypot(ox, oy) || 1; ox /= ol; oy /= ol;
      if (ix * ox + iy * oy < -0.2) {
        // a sharp turn: sweep both sides round the point, through the way the pen was going
        const lIn = angleOf(-iy, ix); const lOut = angleOf(-oy, ox);
        const fwd = angleOf(ix, iy);
        const via = (from, to) => { const s1 = sweep(from, fwd); const s2 = sweep(fwd, to); return [from, from + s1 + s2]; };
        const [la, lb] = via(lIn, lOut);
        left.push([x + Math.cos(la) * r, y + Math.sin(la) * r]); arc(left, x, y, r, la, lb, 8); left.push([x + Math.cos(lb) * r, y + Math.sin(lb) * r]);
        const [ra, rb] = via(lIn + Math.PI, lOut + Math.PI);
        right.push([x + Math.cos(ra) * r, y + Math.sin(ra) * r]); arc(right, x, y, r, ra, rb, 8); right.push([x + Math.cos(rb) * r, y + Math.sin(rb) * r]);
        continue;
      }
    }
    const [dx, dy] = dirAt(i);
    left.push([x - dy * r, y + dx * r]);
    right.push([x + dy * r, y - dx * r]);
  }

  // 4) round caps at both ends
  const out = [...left];
  const [ex, ey] = pts[n - 1]; const er = radii[n - 1];
  const [edx, edy] = dirAt(n - 1);
  const endFrom = angleOf(-edy, edx);
  arc(out, ex, ey, er, endFrom, endFrom - Math.PI, 10);
  for (let i = right.length - 1; i >= 0; i--) out.push(right[i]);
  const [sx, sy] = pts[0]; const sr = radii[0];
  const [sdx, sdy] = dirAt(0);
  const startFrom = angleOf(sdy, -sdx);
  arc(out, sx, sy, sr, startFrom, startFrom - Math.PI, 10);
  return out;
}

// A closed outline → smooth ops (quadratic curves through the midpoints).
export function smoothClosed(poly) {
  if (poly.length < 3) return [];
  const mid = (a, b) => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  const ops = [];
  const m0 = mid(poly[poly.length - 1], poly[0]);
  ops.push(['M', m0[0], m0[1]]);
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i]; const m = mid(p, poly[(i + 1) % poly.length]);
    ops.push(['Q', p[0], p[1], m[0], m[1]]);
  }
  ops.push(['Z']);
  return ops;
}

// An open polyline → smooth ops (the highlighter, the eraser's trail).
export function smoothOpen(points, eps = 0.4) {
  const pts = simplify(points.map((p) => [p[0], p[1]]), eps);
  if (!pts.length) return [];
  const ops = [['M', pts[0][0], pts[0][1]]];
  if (pts.length === 1) { ops.push(['L', pts[0][0] + 0.01, pts[0][1]]); return ops; }
  if (pts.length === 2) { ops.push(['L', pts[1][0], pts[1][1]]); return ops; }
  for (let i = 1; i < pts.length - 1; i++) {
    const p = pts[i]; const nx = pts[i + 1];
    ops.push(['Q', p[0], p[1], (p[0] + nx[0]) / 2, (p[1] + nx[1]) / 2]);
  }
  const e = pts[pts.length - 1];
  ops.push(['L', e[0], e[1]]);
  return ops;
}

// A pressure for a mouse / finger point from how fast it moves (px per ms on
// screen): slow = wider, fast = thinner, eased from the previous point.
export function simulatedPressure(prev, speed) {
  const target = 0.62 - Math.min(1, speed / 2.2) * 0.32;
  return prev == null ? target : prev + (target - prev) * 0.35;
}
